import { bind } from '@myre/common-primitives';
import {
  ToggleButtonComponent,
  ToggleDirective,
  ToggleOnComponent,
  WithToggleDirective,
} from '@myre/ui-primitive-components';

import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  type AfterContentInit,
  type TrackByFunction,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { CheckboxComponent } from '../../components/checkbox/checkbox.component';

import { type LevelItem } from '../level-item.interface';
import { trackByDefault } from '../multilevel-toggle.helpers';

// Hold update levelrow function to be called in row template if needed
interface UpdateCallback {
  updateLevel: (item: LevelItem, data: any) => void;
}

@Component({
  selector: 'multilevel-toggle-line',
  styleUrls: ['./multilevel-toggle-line.component.scss'],
  templateUrl: './multilevel-toggle-line.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    WithToggleDirective,
    ToggleDirective,
    ToggleButtonComponent,
    CheckboxComponent,
    FormsModule,
    NgTemplateOutlet,
    ToggleOnComponent,
  ],
})
export class MultilevelToggleLineComponent implements AfterContentInit {
  // Data of the current row and all children to display
  @Input()
  public set levelRow(levelItem: LevelItem) {
    this._levelRow = levelItem;
    // Only leaf have selected attribute set. Update parent row
    this.updateLevelCheckbox(null, null);
  }

  public get levelRow(): LevelItem {
    return this._levelRow;
  }

  @Input()
  public first: boolean;

  @Input()
  public last: boolean;

  @Input()
  public withPaddingBottom: boolean = false;

  // Emit the change event to update parents row
  @Output()
  public change: EventEmitter<{ item: LevelItem; data?: any }> =
    new EventEmitter<{ item: LevelItem; data?: any }>();

  // TrackBy function used in ngFor. Default return the id
  public trackByFn: TrackByFunction<LevelItem> = trackByDefault;

  // Specify call back for manually update data row outside of the component
  public updateCallback: UpdateCallback = {
    updateLevel: bind(this.updateLevelItem, this),
  };

  private _levelRow: LevelItem;

  public ngAfterContentInit(): void {
    // Update checkboxes states
    this.updateLevelCheckbox(null, null);
  }

  /**
   * Handle changes made on the row data outside the component (in the template provided)
   * Replace data of the levelItem for the row by the one provided.
   * The update is provided to parent row if any
   *
   * @param item levelItem of row on which data will be set
   * @param data the data updated to set for the levelItem
   */
  public updateLevelItem(item: LevelItem, data: any): void {
    this.updateLevelCheckbox(item, data);
  }

  /**
   * Update this row checkbox state after click on leaf's checkbox
   *
   * @param item the levelItem of the row on which the user click
   * @param checked the state of the checkbox after the click
   */
  public updateLeafLevelCheckbox(item: LevelItem, checked: boolean): void {
    item.selected = checked;
    this.updateLevelItem(item, null);
  }

  /**
   * Update the checkbox of the current level by checking the state (checked, indeterminate) of the children's checkbox
   * All checkboxes checked -> set state to checked
   * At least one checkbox checked or with the state indeterminate -> set state to indeterminate
   * No checkbox checked -> set state to no checked
   * When a checkbox triggered an event, the value is not yet updated. Because of this the value of the checkbox
   * on which the user clicked is override in the model by the value of the event (checked)
   * Change is emitted on eventEmitter 'change'
   *
   * @param item the levelItem of the row on which the user clicked on the checkbox
   * @param data contains updated data for this row to give to parent
   */
  public updateLevelCheckbox(item: LevelItem, data?: any): void {
    // If data was provided (we manually tell the component that we updated a leaf)
    if (data) {
      // Check if current levelRow is a leaf, and update data if id match (or check children)
      this._updateLeafIfPresent(this._levelRow, item, data);
    }

    if (item && item.selected) {
      this._levelRow.children
        .filter(li => li.id === item.id)
        .forEach(li => {
          li.selected = item.selected;
        });
    }

    const selected =
      this._levelRow.children.length &&
      this._levelRow.children.every(cb => cb.selected);
    this._levelRow.indeterminate =
      !selected &&
      this._levelRow.children.some(cb => cb.selected || cb.indeterminate);
    this._levelRow.selected = selected;
    this.change.emit({ item: { ...this._levelRow } });
  }

  /**
   * Update children's checkbox to the state 'checked'
   *
   * @param checked the value of the state to set to all children's checkbox
   */
  public updateChildren(checked: boolean): void {
    this._levelRow.indeterminate = false;
    this._levelRow.selected = checked;
    this._levelRow.children = this._levelRow.children.map(e =>
      this._setToChecked(e),
    );
    this.change.emit({ item: this._levelRow });
  }

  /**
   * Set this row (and its children) to the value of the checked state of the current row
   */
  private _setToChecked(cb: LevelItem): LevelItem {
    cb.selected = this._levelRow.selected;
    cb.indeterminate = false;
    cb.children = cb.children.map(e => this._setToChecked(e));

    return { ...cb };
  }

  private _updateLeafIfPresent(
    levelRow: LevelItem,
    item: LevelItem,
    data: any,
  ): void {
    if (levelRow.isLeaf) {
      if (levelRow.id === item.id) {
        levelRow.data = { ...data };
      }

      return;
    }

    levelRow.children.forEach(row =>
      this._updateLeafIfPresent(row, item, data),
    );

    return;
  }
}
